import React, { useState, useEffect } from 'react';
import ImpersonateButton from '../impersonation/components/ImpersonateButton';
import { adminListAppUsers, toUserDate } from '../api/appUsersApi';
import './impersonation/ImpersonationPage.css';

// Helper function to capitalize first letter
const capitalizeFirstLetter = (string) => {
  if (!string) return 'Unknown';
  return string.charAt(0).toUpperCase() + string.slice(1);
};

// Helper function to get tooltip message
const getTooltipMessage = (user) => {
  if (!user.allowImpersonation) {
    return "Impersonation is disabled for this company";
  }
  return "";
};

const ROWS_PER_PAGE = 5;

const ImpersonationPage = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRole, setSelectedRole] = useState('owner');
  const [page, setPage] = useState(0);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await adminListAppUsers({ limit: 2000 });
        const fetchedUsers = (result.users || [])
          .map((userData) => ({
            ...userData,
            lastActive: toUserDate(userData.lastActiveAt ?? userData.lastActive),
            role: userData.role || 'Unknown',
            companyName: userData.companyName || 'No Company',
            allowImpersonation: userData.allowImpersonation !== false,
          }))
          .filter((user) => user.role !== 'admin');
        setUsers(fetchedUsers);
      } catch (error) {
        console.error('Error fetching users:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    // UX: when filters change, reset paging
    setPage(0);
  }, [searchTerm, selectedRole]);

  // Filter users based on search term and selected role
  const filteredUsers = users.filter(user => {
    const matchesSearch = (
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.companyName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const matchesRole = selectedRole === 'all' || user.role === selectedRole;
    return matchesSearch && matchesRole;
  });

  // Pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const paginatedUsers = filteredUsers.slice(
    page * ROWS_PER_PAGE,
    page * ROWS_PER_PAGE + ROWS_PER_PAGE
  );

  const total = filteredUsers.length;
  const startIndex = page * ROWS_PER_PAGE;
  const endIndex = Math.min(startIndex + ROWS_PER_PAGE, total);
  const pageCount = Math.max(1, Math.ceil(total / ROWS_PER_PAGE));
  const canPrev = page > 0;
  const canNext = endIndex < total;

  const onPrev = () => setPage((p) => Math.max(0, p - 1));
  const onNext = () => setPage((p) => Math.min(pageCount - 1, p + 1));

  const selectedRoleLabel =
    selectedRole === 'all' ? 'All roles' : selectedRole === 'owner' ? 'Owners' : 'Managers';

  const getRoleClass = (role) => {
    const safe = String(role || '').toLowerCase();
    if (safe === 'owner') return 'badge badge-owner';
    if (safe === 'manager') return 'badge badge-manager';
    return 'badge badge-default';
  };

  if (loading) {
    return (
      <div className="admin-page impPage">
        <div className="admin-page__header">
          <div>
            <h1 className="admin-page__title">User Impersonation</h1>
            <p className="admin-page__subtitle">
              Securely access customer accounts for troubleshooting and support.
            </p>
          </div>
          <div className="impHeaderRight">
            <span className="impPill">Roles: Owners &amp; Managers</span>
          </div>
        </div>

        <div className="impCard">
          <div className="impFiltersRow">
            <div className="impSeg" aria-label="Role filter">
              <button type="button" className="impSegBtn" aria-pressed="false">
                All
              </button>
              <button type="button" className="impSegBtn" aria-pressed="true">
                Owners
              </button>
              <button type="button" className="impSegBtn" aria-pressed="false">
                Managers
              </button>
            </div>
            <div className="impSearch">
              <label>Search</label>
              <div className="impSkeletonBar" style={{ width: 420, height: 38, borderRadius: 12 }} />
            </div>
          </div>
        </div>

        <div className="table-wrap">
          <table className="data-table impTable" aria-label="Users table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Company</th>
                <th>Role</th>
                <th>Last active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, idx) => (
                <tr key={idx}>
                  <td>
                    <div className="impSkeletonBar" style={{ width: 220 }} />
                  </td>
                  <td>
                    <div className="impSkeletonBar" style={{ width: 180 }} />
                  </td>
                  <td>
                    <div className="impSkeletonBar" style={{ width: 110 }} />
                  </td>
                  <td>
                    <div className="impSkeletonBar" style={{ width: 150 }} />
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <div className="impSkeletonBar" style={{ width: 160, marginLeft: 'auto' }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page impPage">
        <div className="impCard">
          <div style={{ color: '#b91c1c', fontWeight: 700 }}>Error loading users</div>
          <div className="impMuted" style={{ marginTop: 6 }}>
            {String(error)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page impPage">
      <div className="admin-page__header">
        <div>
          <h1 className="admin-page__title">User Impersonation</h1>
          <p className="admin-page__subtitle">
            Securely access customer accounts for troubleshooting and support.
          </p>
        </div>
        <div className="impHeaderRight">
          <span className="impPill">Roles: Owners &amp; Managers</span>
          <span className="impPill">{total} total</span>
        </div>
      </div>

      <div className="impCard" aria-label="Filters">
        <div className="impFiltersRow">
          <div className="impSeg" aria-label="Role filter">
            <button
              type="button"
              className="impSegBtn"
              aria-pressed={selectedRole === 'all' ? 'true' : 'false'}
              onClick={() => setSelectedRole('all')}
            >
              All
            </button>
            <button
              type="button"
              className="impSegBtn"
              aria-pressed={selectedRole === 'owner' ? 'true' : 'false'}
              onClick={() => setSelectedRole('owner')}
            >
              Owners
            </button>
            <button
              type="button"
              className="impSegBtn"
              aria-pressed={selectedRole === 'manager' ? 'true' : 'false'}
              onClick={() => setSelectedRole('manager')}
            >
              Managers
            </button>
          </div>

          <div className="impSearch">
            <label htmlFor="impSearch">Search</label>
            <input
              id="impSearch"
              type="search"
              placeholder="Search by email or company name…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <span className="impPill" style={{ marginLeft: 'auto' }}>
            {selectedRoleLabel}
          </span>
        </div>
      </div>

      <div className="table-wrap">
        <table className="data-table impTable" aria-label="Users table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Company</th>
              <th>Role</th>
              <th>Last active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedUsers.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <div className="impEmpty">No users found.</div>
                </td>
              </tr>
            ) : (
              paginatedUsers.map((user) => {
                const tooltipMessage = getTooltipMessage(user);
                const isDisabled = !user.allowImpersonation;
                const lastActiveText = user.lastActive ? user.lastActive.toLocaleString() : 'Never';

                return (
                  <tr key={user.id}>
                    <td>{user.email || '—'}</td>
                    <td>{user.companyName || 'No Company'}</td>
                    <td>
                      <span className={getRoleClass(user.role)}>{capitalizeFirstLetter(user.role)}</span>
                    </td>
                    <td>
                      <span className={user.lastActive ? '' : 'impMuted'}>{lastActiveText}</span>
                    </td>
                    <td>
                      <span title={isDisabled ? tooltipMessage : ''} style={{ display: 'inline-flex' }}>
                        <ImpersonateButton
                          userId={user.id}
                          companyId={user.companyId}
                          disabled={isDisabled}
                        />
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        <div className="impCard" style={{ borderRadius: 0, boxShadow: 'none', border: 'none', padding: 16 }}>
          <div className="impFooter">
            <div className="impMuted">
              Showing <strong>{total === 0 ? 0 : startIndex + 1}</strong>–<strong>{endIndex}</strong> of{' '}
              <strong>{total}</strong>
            </div>
            <div className="impPager" aria-label="Pagination">
              <button type="button" className="impPagerBtn" onClick={onPrev} disabled={!canPrev}>
                Prev
              </button>
              <span className="impPill" aria-label="Current page">
                Page {Math.min(page + 1, pageCount)} / {pageCount}
              </span>
              <button type="button" className="impPagerBtn" onClick={onNext} disabled={!canNext}>
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImpersonationPage; 